import React, { useState, useEffect, useRef, useCallback } from 'react';

// Slideshow component with advanced features
const SlideshowComponent = ({ 
  images = [
    { src: 'https://picsum.photos/800/600?random=1', alt: 'Slide 1', caption: '' },
    { src: 'https://picsum.photos/800/600?random=2', alt: 'Slide 2', caption: '' },
    { src: 'https://picsum.photos/800/600?random=3', alt: 'Slide 3', caption: '' }
  ],
  autoplay = true,
  autoplayInterval = 5000,
  showControls = true,
  showIndicators = true,
  showCaptions = true,
  height = '96',
  transition = 'fade',
  borderRadius = 'lg',
  shadow = 'lg',
  aspectRatio = '16:9'
}) => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isPlaying, setIsPlaying] = useState(autoplay);
  const intervalRef = useRef(null);
  const slideshowRef = useRef(null);

  // Auto-play functionality
  useEffect(() => {
    if (isPlaying && images.length > 1) {
      intervalRef.current = setInterval(() => {
        setCurrentSlide((prev) => (prev + 1) % images.length);
      }, autoplayInterval);
    } else {
      clearInterval(intervalRef.current);
    }

    return () => clearInterval(intervalRef.current);
  }, [isPlaying, autoplayInterval, images.length]);

  // Pause on hover
  const handleMouseEnter = () => {
    if (autoplay) {
      setIsPlaying(false);
    }
  };

  const handleMouseLeave = () => {
    if (autoplay) {
      setIsPlaying(true);
    }
  };

  // Navigation functions
  const goToSlide = (index) => {
    setCurrentSlide(index);
  };

  const nextSlide = useCallback(() => {
    setCurrentSlide((prev) => (prev + 1) % images.length);
  }, [images.length]);

  const prevSlide = useCallback(() => {
    setCurrentSlide((prev) => (prev - 1 + images.length) % images.length);
  }, [images.length]);

  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (slideshowRef.current && slideshowRef.current.contains(document.activeElement)) {
        if (e.key === 'ArrowLeft') {
          prevSlide();
        } else if (e.key === 'ArrowRight') {
          nextSlide();
        } else if (e.key === ' ') {
          e.preventDefault();
          setIsPlaying(!isPlaying);
        }
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isPlaying, nextSlide, prevSlide]);

  // Helper functions for styling
  const getHeightClass = () => {
    switch (height) {
      case 'auto': return 'h-auto';
      case 'full': return 'h-full';
      case 'screen': return 'h-screen';
      case 'xs': return 'h-32';
      case 'sm': return 'h-48';
      case 'md': return 'h-64';
      case 'lg': return 'h-80';
      case 'xl': return 'h-96';
      case '2xl': return 'h-128';
      case '96': return 'h-96';
      case '128': return 'h-128';
      default: return 'h-96';
    }
  };

  const getBorderRadiusClass = () => {
    switch (borderRadius) {
      case 'none': return '';
      case 'sm': return 'rounded-sm';
      case 'md': return 'rounded-md';
      case 'lg': return 'rounded-lg';
      case 'xl': return 'rounded-xl';
      case '2xl': return 'rounded-2xl';
      case 'full': return 'rounded-full';
      default: return 'rounded-lg';
    }
  };

  const getShadowClass = () => {
    switch (shadow) {
      case 'none': return '';
      case 'sm': return 'shadow-xs';
      case 'md': return 'shadow-md';
      case 'lg': return 'shadow-lg';
      case 'xl': return 'shadow-xl';
      case '2xl': return 'shadow-2xl';
      default: return 'shadow-lg';
    }
  };

  const getAspectRatioClass = () => {
    switch (aspectRatio) {
      case '1:1': return 'aspect-square';
      case '4:3': return 'aspect-4/3';
      case '16:9': return 'aspect-video';
      case '21:9': return 'aspect-21/9';
      case 'auto': return '';
      default: return 'aspect-video';
    }
  };

  if (!images || images.length === 0) {
    return (
      <div className={`w-full ${getBorderRadiusClass()} ${getShadowClass()} bg-gray-100 dark:bg-gray-800 flex items-center justify-center`}>
        <div className={`${getHeightClass()} w-full flex items-center justify-center`}>
          <p className="text-gray-500 dark:text-gray-400">Keine Bilder verfügbar</p>
        </div>
      </div>
    );
  }

  return (
    <div 
      ref={slideshowRef}
      className={`relative w-full ${getBorderRadiusClass()} ${getShadowClass()} overflow-hidden group`}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      role="region"
      aria-label="Bildergalerie"
      tabIndex={0}
    >
      {/* Slides Container */}
      <div className="relative w-full">
        {images.map((image, index) => (
          <div
            key={index}
            className={`transition-opacity duration-500 ${
              index === currentSlide ? 'opacity-100 relative' : 'opacity-0 absolute inset-0'
            }`}
            style={{
              transition: transition === 'slide' 
                ? `transform 0.5s ease-in-out` 
                : 'opacity 0.5s ease-in-out'
            }}
          >
            <img
              src={image.src}
              alt={image.alt}
              className={`${getHeightClass()} w-full object-cover ${getAspectRatioClass()}`}
              onError={(e) => {
                e.target.src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iODAwIiBoZWlnaHQ9IjYwMCIgdmlld0JveD0iMCAwIDgwMCA2MDAiIGZpbGw9Im5vbmUiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+CjxyZWN0IHdpZHRoPSI4MDAiIGhlaWdodD0iNjAwIiBmaWxsPSIjRjNGNEY2Ii8+CjxwYXRoIGQ9Ik0zNTAgMjUwSDQ1MFYzNTBIMzUwVjI1MFoiIGZpbGw9IiM5Q0EzQUYiLz4KPHBhdGggZD0iTTMwMCA0MDBINTAwVjQ1MEgzMDBWNDAwWiIgZmlsbD0iIzlDQTNBRiIvPgo8L3N2Zz4K';
              }}
            />
            {showCaptions && image.caption && (
              <div className="absolute bottom-0 left-0 right-0 bg-black bg-opacity-50 text-white p-4">
                <p className="text-sm">{image.caption}</p>
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Navigation Controls */}
      {showControls && images.length > 1 && (
        <>
          <button
            onClick={prevSlide}
            className="absolute left-4 top-1/2 transform -translate-y-1/2 bg-black bg-opacity-50 hover:bg-opacity-70 text-white p-2 rounded-full transition-all duration-200 opacity-0 group-hover:opacity-100 focus:opacity-100"
            aria-label="Vorheriges Bild"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
          </button>
          <button
            onClick={nextSlide}
            className="absolute right-4 top-1/2 transform -translate-y-1/2 bg-black bg-opacity-50 hover:bg-opacity-70 text-white p-2 rounded-full transition-all duration-200 opacity-0 group-hover:opacity-100 focus:opacity-100"
            aria-label="Nächstes Bild"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </button>
        </>
      )}

      {/* Play/Pause Button */}
      {autoplay && images.length > 1 && (
        <button
          onClick={() => setIsPlaying(!isPlaying)}
          className="absolute top-4 right-4 bg-black bg-opacity-50 hover:bg-opacity-70 text-white p-2 rounded-full transition-all duration-200 opacity-0 group-hover:opacity-100 focus:opacity-100"
          aria-label={isPlaying ? "Pausieren" : "Abspielen"}
        >
          {isPlaying ? (
            <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
              <path d="M6 4h4v16H6V4zm8 0h4v16h-4V4z"/>
            </svg>
          ) : (
            <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
              <path d="M8 5v14l11-7z"/>
            </svg>
          )}
        </button>
      )}

      {/* Indicators */}
      {showIndicators && images.length > 1 && (
        <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 flex space-x-2">
          {images.map((_, index) => (
            <button
              key={index}
              onClick={() => goToSlide(index)}
              className={`w-3 h-3 rounded-full transition-all duration-200 ${
                index === currentSlide
                  ? 'bg-white'
                  : 'bg-white bg-opacity-50 hover:bg-opacity-75'
              }`}
              aria-label={`Gehe zu Bild ${index + 1}`}
            />
          ))}
        </div>
      )}

      {/* Slide Counter */}
      {images.length > 1 && (
        <div className="absolute top-4 left-4 bg-black bg-opacity-50 text-white px-3 py-1 rounded-full text-sm">
          {currentSlide + 1} / {images.length}
        </div>
      )}
    </div>
  );
};

export const Slideshow = {
  fields: {
    images: {
      type: 'array',
      label: 'Bilder',
      arrayFields: {
        src: {
          type: 'text',
          label: 'Bild URL'
        },
        alt: {
          type: 'text',
          label: 'Alt-Text'
        },
        caption: {
          type: 'text',
          label: 'Bildunterschrift'
        }
      },
      getItemSummary: (item) => item.alt || 'Bild'
    },
    autoplay: {
      type: 'radio',
      label: 'Automatisches Abspielen',
      options: [
        { label: 'Aktiviert', value: true },
        { label: 'Deaktiviert', value: false }
      ]
    },
    autoplayInterval: {
      type: 'select',
      label: 'Abspielintervall (ms)',
      options: [
        { label: '2 Sekunden', value: 2000 },
        { label: '3 Sekunden', value: 3000 },
        { label: '5 Sekunden', value: 5000 },
        { label: '10 Sekunden', value: 10000 }
      ]
    },
    showControls: {
      type: 'radio',
      label: 'Navigation anzeigen',
      options: [
        { label: 'Ja', value: true },
        { label: 'Nein', value: false }
      ]
    },
    showIndicators: {
      type: 'radio',
      label: 'Indikatoren anzeigen',
      options: [
        { label: 'Ja', value: true },
        { label: 'Nein', value: false }
      ]
    },
    showCaptions: {
      type: 'radio',
      label: 'Bildunterschriften anzeigen',
      options: [
        { label: 'Ja', value: true },
        { label: 'Nein', value: false }
      ]
    },
    height: {
      type: 'select',
      label: 'Höhe',
      options: [
        { label: 'Automatisch', value: 'auto' },
        { label: 'Vollhöhe', value: 'full' },
        { label: 'Bildschirmhöhe', value: 'screen' },
        { label: 'XS (128px)', value: 'xs' },
        { label: 'SM (192px)', value: 'sm' },
        { label: 'MD (256px)', value: 'md' },
        { label: 'LG (320px)', value: 'lg' },
        { label: 'XL (384px)', value: 'xl' },
        { label: '2XL (512px)', value: '2xl' },
        { label: '96 (384px)', value: '96' },
        { label: '128 (512px)', value: '128' }
      ]
    },
    aspectRatio: {
      type: 'select',
      label: 'Seitenverhältnis',
      options: [
        { label: '4:3 Standard (800x600)', value: '4:3' },
        { label: '16:9 Breitbild', value: '16:9' },
        { label: '1:1 Quadrat', value: '1:1' },
        { label: '3:2 Klassisch', value: '3:2' },
        { label: 'Automatisch', value: 'auto' }
      ]
    },
    transition: {
      type: 'select',
      label: 'Übergangseffekt',
      options: [
        { label: 'Einblenden (Fade)', value: 'fade' },
        { label: 'Schieben (Slide)', value: 'slide' }
      ]
    },
    borderRadius: {
      type: 'select',
      label: 'Abrundung',
      options: [
        { label: 'Keine Abrundung', value: 'none' },
        { label: 'Klein (SM)', value: 'sm' },
        { label: 'Mittel (MD)', value: 'md' },
        { label: 'Groß (LG)', value: 'lg' },
        { label: 'Sehr groß (XL)', value: 'xl' },
        { label: 'Extra groß (2XL)', value: '2xl' },
        { label: 'Vollständig rund', value: 'full' }
      ]
    },
    shadow: {
      type: 'select',
      label: 'Schatten',
      options: [
        { label: 'Kein Schatten', value: 'none' },
        { label: 'Klein (SM)', value: 'sm' },
        { label: 'Mittel (MD)', value: 'md' },
        { label: 'Groß (LG)', value: 'lg' },
        { label: 'Sehr groß (XL)', value: 'xl' },
        { label: 'Extra groß (2XL)', value: '2xl' }
      ]
    }
  },
  defaultProps: {
    images: [
      { src: 'https://picsum.photos/800/600?random=1', alt: 'Slide 1', caption: '' },
      { src: 'https://picsum.photos/800/600?random=2', alt: 'Slide 2', caption: '' },
      { src: 'https://picsum.photos/800/600?random=3', alt: 'Slide 3', caption: '' }
    ],
    autoplay: true,
    autoplayInterval: 5000,
    showControls: true,
    showIndicators: true,
    showCaptions: true,
    height: '96',
    transition: 'fade',
    borderRadius: 'lg',
    shadow: 'lg',
    aspectRatio: '16:9'
  },
  render: SlideshowComponent
};
